import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import shopIndentsApi from '../../services/shopIndentsApi';

/**
 * Citizen redeem → place shop order (parity with Flutter ShopCatalogRequestScreen).
 * Does NOT mark coupon used — Mitra confirm_payment does that.
 */
const RedeemCouponOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const coupon = location.state?.coupon || null;

  const [resolving, setResolving] = useState(true);
  const [shop, setShop] = useState(null);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [customerName, setCustomerName] = useState(user?.name || user?.full_name || '');
  const [customerPhone, setCustomerPhone] = useState(() =>
    shopIndentsApi.normalizePhone(user?.phone || ''),
  );
  const [payUpi, setPayUpi] = useState(() =>
    shopIndentsApi.normalizePhone(user?.phone || ''),
  );
  const [payApp, setPayApp] = useState('phonepe');
  const [submitting, setSubmitting] = useState(false);

  const unitPrice = useMemo(() => {
    if (!coupon) return 0;
    const original = Number(coupon.original_price) || 0;
    const finalP = Number(coupon.final_price) || 0;
    const disc = Number(coupon.discount_amount || coupon.discount_value) || 0;
    if (original > 0) return original;
    if (finalP > 0) return finalP;
    return disc > 0 ? disc : 0;
  }, [coupon]);

  const discountAmt = useMemo(() => {
    if (!coupon) return 0;
    const disc = Number(coupon.discount_amount || coupon.discount_value) || 0;
    const original = Number(coupon.original_price) || 0;
    const finalP = Number(coupon.final_price) || 0;
    if (disc > 0) return disc;
    if (original > finalP && finalP >= 0) return original - finalP;
    return 0;
  }, [coupon]);

  const subtotal = unitPrice * Math.max(1, qty);
  const payable = Math.max(0, subtotal - discountAmt);

  useEffect(() => {
    document.title = 'Redeem coupon — place order | LOCSY';
    if (!coupon) {
      toast.error('Open Redeem from your Coupons wallet');
      navigate('/user/coupons', { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      setResolving(true);
      const resolved = await shopIndentsApi.resolveShop({
        businessUserId: coupon.business_user_id,
        shopId: coupon.shop_id || coupon.created_by,
        shopName: coupon.mitra_name || coupon.shop_name || coupon.partner_id,
      });
      if (cancelled) return;
      setShop(resolved);
      setResolving(false);
      if (!resolved) {
        toast.warning(
          'Shop not found online. You can still show the coupon code at the counter.',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coupon, navigate]);

  const handleSubmit = async () => {
    if (!shop?.id) {
      toast.error('Shop is not available for online order yet');
      return;
    }
    const phone = shopIndentsApi.normalizePhone(customerPhone);
    if (!customerName.trim()) {
      toast.error('Name required');
      return;
    }
    if (phone.length !== 10) {
      toast.error('Valid 10-digit phone required');
      return;
    }
    const pay = String(payUpi || '').trim();
    const payDigits = pay.includes('@')
      ? pay.split('@')[0].replace(/\D/g, '')
      : pay.replace(/\D/g, '');
    if (payDigits.length < 10 && !pay.includes('@')) {
      toast.error('Enter PhonePe / GPay number or UPI ID');
      return;
    }

    setSubmitting(true);
    try {
      const couponId = coupon.id || coupon.coupon_id;
      const couponCode = coupon.code || coupon.coupon_code;
      const title = coupon.name || coupon.coupon_name || 'Offer';
      const items = [
        {
          offeringId: coupon.service_id
            ? `svc:${coupon.service_id}`
            : `coupon:${couponId}`,
          name: title,
          quantity: Math.max(1, Number(qty) || 1),
          unitPrice,
        },
      ];
      const noteParts = [
        note.trim(),
        `Redeem coupon ${couponCode} (${title})`,
        `Web order · est. ₹${subtotal.toFixed(0)}${
          discountAmt > 0 ? ` · after coupon ₹${payable.toFixed(0)}` : ''
        }`,
        `Pay app: ${payApp}`,
      ].filter(Boolean);

      await shopIndentsApi.create({
        businessId: shop.id,
        businessName: shop.name,
        items,
        note: noteParts.join(' · '),
        customerName: customerName.trim(),
        customerPhone: phone,
        customerUpi: pay,
        customerPayApp: payApp,
        couponId,
        couponCode,
        deductions:
          couponId && discountAmt > 0
            ? [
                {
                  label: `Coupon ${couponCode}`,
                  amount: discountAmt,
                  couponId,
                  couponCode,
                },
              ]
            : undefined,
      });

      toast.success(
        'Order sent! Shop will accept or decline. Coupon applies when they confirm payment.',
      );
      navigate('/user/orders', { replace: true });
    } catch (e) {
      toast.error(e?.message || 'Could not send order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!coupon) return null;

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        Redeem → place order
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Same as the Locsy app: send this offer as an order to the shop. Your coupon
        stays active until the shop confirms payment. Offers are from Mitras — Locsy
        is not liable if a shop declines or edits the deal.
      </Typography>

      {resolving ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {!shop && (
            <Alert severity="warning">
              Shop not listed for online order. Show code{' '}
              <strong>{coupon.code || coupon.coupon_code}</strong> at the counter,
              or try again later.
            </Alert>
          )}

          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={700}>
                {shop?.name ||
                  coupon.mitra_name ||
                  coupon.shop_name ||
                  'Shop'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {coupon.name || coupon.coupon_name}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Est. ₹{subtotal.toFixed(0)}
                {discountAmt > 0
                  ? ` · Coupon −₹${discountAmt.toFixed(0)} · Pay ≈ ₹${payable.toFixed(0)}`
                  : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Code unlocks after you scratch in Coupons. Final bill is set by the shop.
              </Typography>
            </CardContent>
          </Card>

          <TextField
            label="Quantity"
            type="number"
            inputProps={{ min: 1, max: 99 }}
            value={qty}
            onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
            fullWidth
          />
          <TextField
            label="Your name"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Contact phone"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="PhonePe / GPay number or UPI"
            value={payUpi}
            onChange={(e) => setPayUpi(e.target.value)}
            fullWidth
            required
            helperText="Shop can request payment to this number"
          />
          <FormControl fullWidth>
            <InputLabel>Preferred pay app</InputLabel>
            <Select
              label="Preferred pay app"
              value={payApp}
              onChange={(e) => setPayApp(e.target.value)}
            >
              <MenuItem value="phonepe">PhonePe</MenuItem>
              <MenuItem value="gpay">GPay</MenuItem>
              <MenuItem value="paytm">Paytm</MenuItem>
              <MenuItem value="any">Any UPI</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Note for shop (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <Alert severity="info" sx={{ fontSize: 13 }}>
            By sending, you ask the shop to honour this Mitra offer subject to stock and
            their confirmation. No cash refund from Locsy for unused coupons.
          </Alert>

          <Divider />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="outlined"
              onClick={() => navigate('/user/coupons')}
              disabled={submitting}
            >
              Back to coupons
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !shop}
              sx={{ bgcolor: '#FF6A00', '&:hover': { bgcolor: '#e85f00' } }}
            >
              {submitting ? 'Sending…' : 'Send my order'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default RedeemCouponOrder;
